import { Folder } from '@/types'

/**
 * Utility functions for hash-based folder navigation
 */

/**
 * Convert a folder path to a hash fragment
 * Example: "/Contratos/Diciembre" becomes "#/Contratos/Diciembre"
 * Example: "/Castillo Leonardo Inversiones SRL/DGII" becomes "#/Castillo%20Leonardo%20Inversiones%20SRL/DGII"
 */
export function folderPathToHash(folderPath: string): string {
  // Remove leading slash if present
  const cleanPath = folderPath.startsWith('/') ? folderPath.slice(1) : folderPath

  if (!cleanPath) {
    return '#/'
  }

  // Split path into segments, encode each segment separately, then rejoin
  const pathSegments = cleanPath.split('/')
  const encodedSegments = pathSegments.map((segment) => encodeURIComponent(segment))
  const encodedPath = encodedSegments.join('/')

  return `#/${encodedPath}`
}

/**
 * Convert a hash fragment to a folder path
 * Example: "#/Contratos/Diciembre" becomes "/Contratos/Diciembre"
 * Example: "#/Castillo%20Leonardo%20Inversiones%20SRL/DGII" becomes "/Castillo Leonardo Inversiones SRL/DGII"
 */
export function hashToFolderPath(hash: string): string {
  // Remove hash prefix
  const cleanHash = hash.startsWith('#') ? hash.slice(1) : hash

  if (!cleanHash || cleanHash === '/') {
    return '/'
  }

  // Remove leading slash if present for processing
  const pathWithoutLeadingSlash = cleanHash.startsWith('/') ? cleanHash.slice(1) : cleanHash

  // Split into segments, decode each segment, then rejoin
  const pathSegments = pathWithoutLeadingSlash.split('/')
  const decodedSegments = pathSegments.map((segment) => {
    try {
      return decodeURIComponent(segment)
    } catch (error) {
      console.warn('Failed to decode URL segment:', segment, error)
      return segment // Return original segment if decoding fails
    }
  })
  const decodedPath = '/' + decodedSegments.join('/')

  return decodedPath
}

/**
 * Get the current folder path from URL hash
 */
export function getCurrentFolderPathFromHash(): string {
  const hash = window.location.hash
  return hashToFolderPath(hash)
}

/**
 * Set the URL hash based on folder path
 */
export function setHashFromFolderPath(folderPath: string): void {
  const hash = folderPathToHash(folderPath)
  // Use replaceState to avoid adding to browser history for every folder click
  const newUrl = `${window.location.pathname}${hash}`
  window.history.replaceState(null, '', newUrl)
}

/**
 * Find a folder by its path in the folder tree
 */
export function findFolderByPath(folders: Folder[], targetPath: string): Folder | null {
  // Handle root folder case
  if (!targetPath || targetPath === '/') {
    return null // null represents root folder
  }

  // Recursively search through the folder tree
  for (const folder of folders) {
    if (folder.path === targetPath) {
      return folder
    }

    // Search in subfolders
    if (folder.subfolders && folder.subfolders.length > 0) {
      const found = findFolderByPath(folder.subfolders, targetPath)
      if (found) return found
    }

    // Search in children (alternative naming)
    if (folder.children && folder.children.length > 0) {
      const found = findFolderByPath(folder.children, targetPath)
      if (found) return found
    }
  }

  return null
}

/**
 * Get all parent folder IDs for a given folder path
 * This is useful for expanding the folder tree to show the target folder
 */
export function getParentIdsFromPath(folders: Folder[], targetPath: string): number[] {
  const targetFolder = findFolderByPath(folders, targetPath)
  if (!targetFolder) return []

  const parentIds: number[] = []
  let currentPath = targetPath

  // Work backwards through the path to find all parent folders
  while (currentPath && currentPath !== '/') {
    // Find the parent path by removing the last segment
    const pathSegments = currentPath.split('/').filter(Boolean)
    if (pathSegments.length > 1) {
      pathSegments.pop() // Remove last segment
      const parentPath = '/' + pathSegments.join('/')
      const parentFolder = findFolderByPath(folders, parentPath)
      if (parentFolder) {
        parentIds.unshift(parentFolder.id) // Add to beginning
      }
      currentPath = parentPath
    } else {
      break
    }
  }

  return parentIds
}

/**
 * Navigate to a folder and update the URL hash
 */
export function navigateToFolderWithHash(folder: Folder | null): void {
  const folderPath = folder?.path || '/'
  setHashFromFolderPath(folderPath)
}

/**
 * Listen for hash changes and return the new folder path
 */
export function onHashChange(callback: (folderPath: string) => void): () => void {
  const handleHashChange = () => {
    const folderPath = getCurrentFolderPathFromHash()
    callback(folderPath)
  }

  window.addEventListener('hashchange', handleHashChange)

  // Return cleanup function
  return () => {
    window.removeEventListener('hashchange', handleHashChange)
  }
}
